import { createBrowserRouter, Navigate, useRouteError, useNavigate } from 'react-router-dom'
import { Button } from './components/ui/Button'
import { Shell } from './components/layout/Shell'
import { useAuthStore } from './store/auth'
import Login from './pages/Login'
import Setup from './pages/Setup'
import Onboarding from './pages/Onboarding'
import AssetsPage from './pages/Assets'
import Dashboard from './pages/Dashboard'
import Clients from './pages/Clients'
import ClientDetail from './pages/ClientDetail'
import Projects from './pages/Projects'
import ProjectDetail from './pages/ProjectDetail'
import TimeTracking from './pages/TimeTracking'
import Tasks from './pages/Tasks'
import Catalog from './pages/Catalog'
import Proposals from './pages/Proposals'
import ProposalDetail from './pages/ProposalDetail'
import Invoices from './pages/Invoices'
import InvoiceDetail from './pages/InvoiceDetail'
import Deals from './pages/Deals'
import PurchaseOrders from './pages/PurchaseOrders'
import FloorPlans from './pages/FloorPlans'
import Settings from './pages/Settings'
import ActivityLog from './pages/ActivityLog'
import Users from './pages/Users'
import FloorPlanDesigner from './pages/FloorPlanDesigner'
import ProjectCosts from './pages/ProjectCosts'
import MarkupRules from './pages/MarkupRules'
import ClientProposalView from './pages/ClientProposalView'
import Pipeline from './pages/Pipeline'
import Sites from './pages/Sites'
import SiteDetail from './pages/SiteDetail'
import Dispatch from './pages/Dispatch'
import WorkOrderDetail from './pages/WorkOrderDetail'
import NetworkOverview from './pages/NetworkOverview'
import PlatformAdmin from './pages/PlatformAdmin'
import KnowledgeBase from './pages/KnowledgeBase'
import TicketsPage from './pages/Tickets'
import ChatDashboardPage from './pages/ChatDashboard'
import DeviceModuleList from './pages/DeviceModuleList'
import DeviceModuleDesigner from './pages/DeviceModuleDesigner'
import Contracts from './pages/Contracts'
import Commissions from './pages/Commissions'
import KBArticlePage from './pages/KBArticlePage'
import KBArticleEditPage from './pages/KBArticleEditPage'
import PublicKBArticle from './pages/PublicKBArticle'
import ProxyViewer from './pages/ProxyViewer'
import DesktopViewer from './pages/DesktopViewer'
import CloudInfrastructure from './pages/CloudInfrastructure'
import VMSHealth from './pages/VMSHealth'
import Workflows from './pages/Workflows'
import WorkflowEditor from './pages/WorkflowEditor'
import DWOAuthCallback from './pages/DWOAuthCallback'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RouteErrorBoundary() {
  const error = useRouteError()
  const navigate = useNavigate()
  const userRole = useAuthStore.getState().user?.role
  const isAdmin = userRole === 'super_admin' || userRole === 'admin'

  // Log full error details to console for debugging
  console.error('[RouteErrorBoundary]', {
    error,
    type: typeof error,
    constructor: error?.constructor?.name,
    message: error instanceof Error ? error.message : undefined,
    status: (error as { status?: number })?.status,
    statusText: (error as { statusText?: string })?.statusText,
    data: (error as { data?: unknown })?.data,
  })

  const message = error instanceof Error ? error.message
    : (error as { statusText?: string })?.statusText || 'An unexpected error occurred'

  return (
    <div style={{ padding: 40, maxWidth: 600, margin: '80px auto', textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>!</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Something went wrong</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>{message}</p>
      {isAdmin && (
        <pre style={{
          textAlign: 'left', fontSize: 11, padding: 12, borderRadius: 8,
          background: 'var(--bg-inset)', color: 'var(--text-tertiary)',
          overflow: 'auto', maxHeight: 200, marginBottom: 24, cursor: 'copy',
        }}
          onClick={() => navigator.clipboard?.writeText(
            error instanceof Error ? (error.stack || error.message)
            : JSON.stringify(error, null, 2)
          )}
          title="Click to copy"
        >
          {error instanceof Error ? error.stack : `[${error?.constructor?.name || typeof error}] ${JSON.stringify(error, null, 2)}`}
        </pre>
      )}
      <Button onClick={() => navigate(-1)}>Go Back</Button>
    </div>
  )
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/setup',
    element: <Setup />,
  },
  {
    path: '/p/:token',
    element: <ClientProposalView />,
  },
  {
    path: '/help/:slug',
    element: <PublicKBArticle />,
  },
  {
    path: '/integrations/dw/callback',
    element: <DWOAuthCallback />,
  },
  {
    path: '/onboarding',
    element: <RequireAuth><Onboarding /></RequireAuth>,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      // Primary routes (shown in sidebar)
      { index: true, element: <Dashboard /> },
      { path: 'clients', element: <Clients /> },
      { path: 'clients/:slug', element: <ClientDetail /> },
      { path: 'sites', element: <Sites /> },
      { path: 'sites/:slug', element: <SiteDetail /> },
      { path: 'network', element: <NetworkOverview /> },
      { path: 'cloud', element: <CloudInfrastructure /> },
      { path: 'vms', element: <VMSHealth /> },
      { path: 'proposals', element: <Proposals /> },
      { path: 'proposals/:slug', element: <ProposalDetail /> },
      { path: 'dispatch', element: <Dispatch /> },
      { path: 'dispatch/:slug', element: <WorkOrderDetail /> },
      { path: 'pipeline', element: <Pipeline /> },
      { path: 'invoices', element: <Invoices /> },
      { path: 'invoices/:slug', element: <InvoiceDetail /> },
      { path: 'products', element: <Catalog /> },
      { path: 'settings', element: <Settings /> },
      { path: 'settings/*', element: <Settings /> },
      { path: 'admin', element: <Navigate to="/platform" replace /> },
      { path: 'admin/*', element: <Navigate to="/platform" replace /> },
      // NOTE: /platform routes should be guarded by role (super_admin only). Auth is handled by the Shell.
      { path: 'platform', element: <PlatformAdmin /> },
      { path: 'platform/device-modules/:id/design', element: <DeviceModuleDesigner /> },
      { path: 'platform/*', element: <PlatformAdmin /> },
      { path: 'contracts', element: <Contracts /> },
      { path: 'commissions', element: <Commissions /> },
      { path: 'assets', element: <AssetsPage /> },
      { path: 'kb', element: <KnowledgeBase /> },
      { path: 'kb/:slug', element: <KBArticlePage /> },
      { path: 'kb/:slug/edit', element: <KBArticleEditPage /> },
      { path: 'tickets', element: <TicketsPage /> },
      { path: 'chat', element: <ChatDashboardPage /> },
      { path: 'workflows', element: <Workflows /> },
      { path: 'workflows/:id/edit', element: <WorkflowEditor /> },

      // Accessible but NOT in sidebar
      { path: 'floor-plans', element: <FloorPlans /> },
      { path: 'floor-plans/:slug', element: <FloorPlanDesigner />, errorElement: <RouteErrorBoundary /> },
      { path: 'site-plans/:slug', element: <FloorPlanDesigner />, errorElement: <RouteErrorBoundary /> },
      { path: 'projects', element: <Projects /> },
      { path: 'projects/:slug', element: <ProjectDetail /> },
      { path: 'time', element: <TimeTracking /> },
      { path: 'tasks', element: <Tasks /> },
      { path: 'deals', element: <Deals /> },
      { path: 'purchase-orders', element: <PurchaseOrders /> },

      // Legacy/utility routes
      { path: 'catalog', element: <Catalog /> },
      { path: 'project-costs', element: <ProjectCosts /> },
      { path: 'markup-rules', element: <MarkupRules /> },
      { path: 'activity', element: <ActivityLog /> },
      { path: 'users', element: <Users /> },
      { path: 'proxy', element: <ProxyViewer /> },
      { path: 'desktop/:sessionId', element: <DesktopViewer /> },
    ],
  },
])
